import React, { useMemo, useState } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import { BarChart3, CheckCircle2, Eye, Inbox, PieChart as PieChartIcon } from "lucide-react";
import { format, startOfDay, subDays } from "date-fns";
import { el } from "date-fns/locale";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import PartnerPageHeader from "@/components/partners/PartnerPageHeader";
import PartnerTimeFilter, { TimeRangeType } from "@/components/partners/PartnerTimeFilter";
import PartnerMetricCard from "@/components/partners/PartnerMetricCard";
import { FriendlyIllustration } from "@/components/ui/FriendlyIllustration";
import { Skeleton } from "@/components/ui/skeleton";
import { usePartnerLeads } from "@/hooks/usePartnerLeads";
import { PARTNER_STAGES, PartnerOutletContext, SERVICE_LABELS, parsePortalDate } from "@/types/partnerPortal";

const startForRange = (range: TimeRangeType) => {
  if (range === "today") return startOfDay(new Date());
  if (range === "7d") return subDays(new Date(), 7);
  if (range === "30d") return subDays(new Date(), 30);
  if (range === "90d") return subDays(new Date(), 90);
  return new Date(0);
};

const AnalyticsTooltip = ({ active, payload, label }: { active?: boolean; payload?: Array<{ value: number }>; label?: string }) => {
  if (!active || !payload?.length) return null;
  return <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs shadow-lg"><p className="font-semibold text-slate-700">{label}</p><p className="mt-1 text-sky-700">{payload[0].value} leads</p></div>;
};

const PartnerAnalyticsPage = () => {
  const { partnerId } = useParams<{ partnerId: string }>();
  const { partner } = useOutletContext<PartnerOutletContext>();
  const { leads, isLoading, error } = usePartnerLeads(partnerId);
  const [timeRange, setTimeRange] = useState<TimeRangeType>("30d");

  const filteredLeads = useMemo(() => {
    const start = startForRange(timeRange);
    return leads.filter((lead) => parsePortalDate(lead.createdAt) >= start);
  }, [leads, timeRange]);

  const metrics = useMemo(() => {
    const total = filteredLeads.length;
    const viewed = filteredLeads.filter((lead) => lead.linkOpened).length;
    const closed = filteredLeads.filter((lead) => lead.stage === "έκλεισεΗΔουλειά").length;
    const active = filteredLeads.filter((lead) => lead.stage !== "έκλεισεΗΔουλειά").length;
    return {
      total,
      viewed,
      closed,
      active,
      viewedRate: total ? Math.round((viewed / total) * 100) : 0,
      closeRate: total ? Math.round((closed / total) * 100) : 0,
    };
  }, [filteredLeads]);

  const trendData = useMemo(() => {
    const monthly = timeRange === "all";
    const buckets = new Map<string, { date: Date; count: number }>();
    filteredLeads.forEach((lead) => {
      const date = parsePortalDate(lead.createdAt);
      const key = format(date, monthly ? "yyyy-MM" : "yyyy-MM-dd");
      const existing = buckets.get(key);
      buckets.set(key, { date, count: (existing?.count ?? 0) + 1 });
    });
    return Array.from(buckets.values())
      .sort((a, b) => a.date.getTime() - b.date.getTime())
      .map((item) => ({ label: format(item.date, monthly ? "MMM yy" : "d MMM", { locale: el }), leads: item.count }));
  }, [filteredLeads, timeRange]);

  const stageData = useMemo(() => PARTNER_STAGES.map((stage) => {
    const count = filteredLeads.filter((lead) => lead.stage === stage.key).length;
    return { ...stage, count, percentage: metrics.total ? Math.round((count / metrics.total) * 100) : 0 };
  }), [filteredLeads, metrics.total]);

  const serviceData = useMemo(() => {
    const counts = new Map<string, number>();
    filteredLeads.forEach((lead) => counts.set(lead.service, (counts.get(lead.service) ?? 0) + 1));
    return Array.from(counts.entries())
      .map(([service, count]) => ({ service, label: SERVICE_LABELS[service] || service, count, percentage: metrics.total ? Math.round((count / metrics.total) * 100) : 0 }))
      .sort((a, b) => b.count - a.count);
  }, [filteredLeads, metrics.total]);

  const topService = serviceData[0];

  return (
    <div className="space-y-6">
      <PartnerPageHeader
        eyebrow="Μετρήσιμη εικόνα"
        title="Απόδοση"
        description="Κατανοήστε τον όγκο, την προβολή και την έκβαση των leads που σας έχουν ανατεθεί."
        actions={<PartnerTimeFilter value={timeRange} onChange={setTimeRange} />}
      />

      {error && <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm font-medium text-rose-800">{error}</div>}

      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <PartnerMetricCard label="Leads περιόδου" value={metrics.total} hint="Νέες αναθέσεις" icon={<Inbox className="h-5 w-5" />} tone="blue" />
        <PartnerMetricCard label="Προβλήθηκαν" value={`${metrics.viewedRate}%`} hint={`${metrics.viewed} από ${metrics.total}`} icon={<Eye className="h-5 w-5" />} tone="violet" />
        <PartnerMetricCard label="Έκλεισαν" value={metrics.closed} hint={`${metrics.closeRate}% του συνόλου`} icon={<CheckCircle2 className="h-5 w-5" />} tone="emerald" />
        <PartnerMetricCard label="Ανοιχτά" value={metrics.active} hint="Χρειάζονται συνέχεια" icon={<BarChart3 className="h-5 w-5" />} tone="amber" />
      </div>

      {isLoading ? (
        <div className="grid gap-4 lg:grid-cols-2"><Skeleton className="h-80 rounded-2xl" /><Skeleton className="h-80 rounded-2xl" /></div>
      ) : metrics.total === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-5 py-14 text-center">
          <FriendlyIllustration kind="lead-insights" className="mx-auto h-36 w-auto max-w-[230px] drop-shadow-[0_12px_20px_rgba(14,116,190,0.12)]" />
          <h2 className="mt-2 font-bold text-slate-900">Δεν υπάρχουν δεδομένα</h2>
          <p className="mt-1 text-sm text-slate-500">Επιλέξτε μεγαλύτερη χρονική περίοδο.</p>
        </div>
      ) : (
        <>
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1.35fr)_minmax(320px,0.65fr)]">
            <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
              <div className="mb-5">
                <h2 className="font-bold text-slate-950">Ροή νέων leads</h2>
                <p className="mt-1 text-xs text-slate-500">Πότε δημιουργήθηκαν οι αναθέσεις της περιόδου.</p>
              </div>
              <div className="h-64 w-full sm:h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={trendData} margin={{ top: 8, right: 4, left: -24, bottom: 0 }}>
                    <defs><linearGradient id="partnerTrend" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#0284c7" stopOpacity={0.24} /><stop offset="100%" stopColor="#0284c7" stopOpacity={0.02} /></linearGradient></defs>
                    <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#64748b" }} tickLine={false} axisLine={false} minTickGap={28} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#64748b" }} tickLine={false} axisLine={false} />
                    <Tooltip content={<AnalyticsTooltip />} />
                    <Area type="monotone" dataKey="leads" stroke="#0284c7" strokeWidth={2.5} fill="url(#partnerTrend)" activeDot={{ r: 4 }} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-sky-50 text-sky-700"><PieChartIcon className="h-5 w-5" /></div>
                <div>
                  <h2 className="font-bold text-slate-950">Κυρίαρχη ζήτηση</h2>
                  <p className="mt-1 text-xs text-slate-500">Υπηρεσίες που ζητούν περισσότερο οι πελάτες.</p>
                </div>
              </div>
              {topService && (
                <div className="mt-5 rounded-xl bg-slate-950 p-4 text-white">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Πρώτη υπηρεσία</p>
                  <p className="mt-1 text-lg font-bold">{topService.label}</p>
                  <p className="mt-1 text-sm text-slate-300">{topService.count} leads · {topService.percentage}%</p>
                </div>
              )}
              <div className="mt-5 space-y-4">
                {serviceData.slice(0, 6).map((item) => (
                  <div key={item.service}>
                    <div className="mb-1.5 flex items-center justify-between gap-3 text-xs"><span className="truncate font-medium text-slate-700">{item.label}</span><span className="font-bold text-slate-900">{item.count}</span></div>
                    <div className="h-2 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-sky-500" style={{ width: `${item.percentage}%` }} /></div>
                  </div>
                ))}
              </div>
            </section>
          </div>

          <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
            <div className="mb-5">
              <h2 className="font-bold text-slate-950">Τρέχουσα κατανομή pipeline</h2>
              <p className="mt-1 text-xs leading-5 text-slate-500">Δείχνει σε ποιο στάδιο βρίσκονται τώρα τα leads· δεν αποτελεί ιστορικό funnel μετατροπών.</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {stageData.map((stage) => (
                <div key={stage.key} className="rounded-xl border border-slate-200 p-4">
                  <div className="flex items-center justify-between gap-2"><span className="flex items-center gap-2 text-xs font-semibold text-slate-600"><span className={`h-2.5 w-2.5 rounded-full ${stage.dotClass}`} />{stage.label}</span><span className="text-xl font-bold text-slate-950">{stage.count}</span></div>
                  <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-100"><div className={`h-full rounded-full ${stage.dotClass}`} style={{ width: `${stage.percentage}%` }} /></div>
                  <p className="mt-2 text-xs text-slate-500">{stage.percentage}% του συνόλου</p>
                </div>
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default PartnerAnalyticsPage;
